import { useContext, useEffect, useState } from "react";
import CalendarIcon from "@mui/icons-material/CalendarMonth";
import DrivingIcon from "@mui/icons-material/DirectionsCar";
import EventIcon from "@mui/icons-material/Event";
import GroupIcon from "@mui/icons-material/Group";
import LocationIcon from "@mui/icons-material/LocationOn";
import NoteIcon from "@mui/icons-material/NoteAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonIcon from "@mui/icons-material/Person";
import TransitIcon from "@mui/icons-material/Train";
import {
  Box,
  Button,
  Chip,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import type {
  DoctorInterviewView,
  RouteTimeResult,
} from "@thalamos/aspire-common";

import { api } from "~/api.js";
import {
  PopupDialog,
  PopupDialogTitle,
} from "~/components/design-system/index.js";
import { config } from "~/config.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { formatInterviewTimeRange } from "~/util.js";
import { downloadIcsFile } from "~/utils/downloadIcsFile.js";

// ---------------------------------------------------------------------------
// API interface for dependency injection (testing / stories)
// ---------------------------------------------------------------------------

export interface InterviewDetailsDialogApi {
  getRouteTimes: (
    interviewId: string,
    body: { userIds: string[] },
  ) => Promise<{ data: RouteTimeResult[] }>;
}

const defaultApi: InterviewDetailsDialogApi = {
  getRouteTimes: (interviewId, body) =>
    api.interviews.getRouteTimes(interviewId, body),
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface InterviewDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  interview: DoctorInterviewView;
  label: string;
  /** Optional API override for testing / Storybook */
  apiOverride?: InterviewDetailsDialogApi;
  /** Optional user override for testing (bypasses context) */
  userIdOverride?: string;
  /** Optional doctor postal code override for testing */
  doctorPostalCodeOverride?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InterviewDetailsDialog({
  open,
  onClose,
  interview,
  label,
  apiOverride,
  userIdOverride,
  doctorPostalCodeOverride,
}: InterviewDetailsDialogProps) {
  const apiClient = apiOverride ?? defaultApi;
  const currentUserContext = useContext(LoggedInUserContext);
  const user = currentUserContext?.user;

  const userId = userIdOverride ?? user?.id;
  const doctorPostalCode =
    doctorPostalCodeOverride !== undefined
      ? doctorPostalCodeOverride
      : (user?.homeBaseAddress?.postalCode ?? null);

  const [travelMode, setTravelMode] = useState<"driving" | "transit">(
    "driving",
  );
  const [routeTime, setRouteTime] = useState<RouteTimeResult | null>(null);
  const [routeTimeLoading, setRouteTimeLoading] = useState(false);

  // Fetch route time when dialog opens
  useEffect(() => {
    if (!open || !userId || !interview.postalCode) return;

    setRouteTimeLoading(true);
    apiClient
      .getRouteTimes(interview.id, { userIds: [userId] })
      .then((result) => {
        const match = result.data.find((r) => r.doctorId === userId);
        setRouteTime(match ?? null);
      })
      .catch(() => {
        // Route time is a nice-to-have — fail silently
      })
      .finally(() => {
        setRouteTimeLoading(false);
      });
  }, [open, userId, interview.id, interview.postalCode, apiClient]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setRouteTime(null);
      setTravelMode("driving");
    }
  }, [open]);

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const hasAddress =
    !interview.locationUnknown &&
    (!!interview.address || !!interview.postalCode);
  const assessmentLocation = hasAddress
    ? `${interview.address ?? ""}${
        interview.address && interview.postalCode
          ? `, ${interview.postalCode}`
          : (interview.postalCode ?? "")
      }`
    : null;

  const selectedPolyline =
    travelMode === "driving"
      ? routeTime?.drivingPolyline
      : routeTime?.transitPolyline;

  const staticMapUrl =
    assessmentLocation && config.googleMapsApiKey !== ""
      ? (() => {
          const params = new URLSearchParams({
            size: "600x300",
            scale: "2",
            key: config.googleMapsApiKey,
            maptype: "roadmap",
          });
          params.append("markers", `color:red|label:A|${assessmentLocation}`);
          if (doctorPostalCode) {
            params.append(
              "markers",
              `color:green|label:H|${doctorPostalCode}, UK`,
            );
          }
          if (selectedPolyline) {
            params.append(
              "path",
              `color:0x4285F4FF|weight:4|enc:${selectedPolyline}`,
            );
          }
          return `https://maps.googleapis.com/maps/api/staticmap?${params.toString()}`;
        })()
      : null;

  const directionsUrl =
    assessmentLocation && doctorPostalCode
      ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(`${doctorPostalCode}, UK`)}&destination=${encodeURIComponent(assessmentLocation)}&travelmode=${travelMode}`
      : assessmentLocation
        ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(assessmentLocation)}`
        : null;

  const selectedTravelTime =
    travelMode === "driving"
      ? routeTime?.drivingTimeMinutes
      : routeTime?.transitTimeMinutes;

  const canAddToCalendar = interview.timeRanges.length === 1;

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleDownloadCalendarInvite = () => {
    const range = interview.timeRanges[0];
    if (!range) return;

    downloadIcsFile({
      start: range.start,
      end: range.end,
      summary: "MHA Assessment",
      location: assessmentLocation,
      description: `MHA Assessment — ${interview.amhpTeamName}`,
    });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <PopupDialog
      open={open}
      onClose={onClose}
      testId="interview-details-dialog"
    >
      <PopupDialogTitle titleText={label} closeDialog={onClose} />

      {/* Static Map */}
      {assessmentLocation && (
        <Box sx={{ mb: 3 }}>
          {staticMapUrl && (
            <Box
              component={directionsUrl ? "a" : "div"}
              href={directionsUrl ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                borderRadius: 2,
                overflow: "hidden",
                border: "1px solid",
                borderColor: "divider",
                cursor: directionsUrl ? "pointer" : "default",
                textDecoration: "none",
                "&:hover": directionsUrl ? { borderColor: "primary.main" } : {},
              }}
            >
              <Box
                component="img"
                src={staticMapUrl}
                alt="Assessment location map"
                sx={{ width: "100%", height: "auto", display: "block" }}
              />
            </Box>
          )}

          {/* Travel mode toggle + time + directions link */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ToggleButtonGroup
                value={travelMode}
                exclusive
                onChange={(_, value: string | null) => {
                  if (value === "driving" || value === "transit") {
                    setTravelMode(value);
                  }
                }}
                size="small"
                data-testid="travel-mode-toggle"
              >
                <ToggleButton
                  value="driving"
                  aria-label="Driving"
                  data-testid="travel-mode-driving"
                >
                  <DrivingIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton
                  value="transit"
                  aria-label="Public transport"
                  data-testid="travel-mode-transit"
                >
                  <TransitIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>

              {routeTimeLoading && (
                <Skeleton
                  variant="rounded"
                  width={100}
                  height={24}
                  sx={{ borderRadius: 2 }}
                />
              )}
              {!routeTimeLoading && selectedTravelTime != null && (
                <Chip
                  icon={
                    travelMode === "driving" ? (
                      <DrivingIcon sx={{ fontSize: "0.875rem" }} />
                    ) : (
                      <TransitIcon sx={{ fontSize: "0.875rem" }} />
                    )
                  }
                  label={`~${String(selectedTravelTime)} min`}
                  size="small"
                  variant="outlined"
                  color="info"
                  data-testid="travel-time-chip"
                  sx={{
                    height: 24,
                    "& .MuiChip-label": { px: 0.75, fontSize: "0.8rem" },
                  }}
                />
              )}
            </Box>

            {directionsUrl && (
              <Button
                size="small"
                href={directionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewIcon fontSize="small" />}
                sx={{ textTransform: "none", fontSize: "0.8rem" }}
                data-testid="get-directions-link"
              >
                Get Directions
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* Assessment details */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* AMHP team */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <GroupIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              AMHP Team
            </Typography>
            <Typography variant="body2" data-testid="amhp-team-name">
              {interview.amhpTeamName}
            </Typography>
          </Box>
        </Box>

        {/* AMHP contact */}
        {(interview.amhpName ?? interview.amhpTeamEmail) && (
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <PersonIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block" }}
              >
                AMHP Contact
              </Typography>
              {interview.amhpName ? (
                <>
                  <Typography variant="body2" data-testid="amhp-name">
                    {interview.amhpName}
                  </Typography>
                  {interview.amhpEmail && (
                    <Typography
                      variant="body2"
                      component="a"
                      href={`mailto:${interview.amhpEmail}`}
                      data-testid="amhp-email"
                      sx={{ color: "primary.main" }}
                    >
                      {interview.amhpEmail}
                    </Typography>
                  )}
                </>
              ) : interview.amhpTeamEmail ? (
                <Typography
                  variant="body2"
                  component="a"
                  href={`mailto:${interview.amhpTeamEmail}`}
                  data-testid="amhp-team-email"
                  sx={{ color: "primary.main" }}
                >
                  {interview.amhpTeamEmail}
                </Typography>
              ) : null}
            </Box>
          </Box>
        )}

        {/* Note from AMHP */}
        {interview.myParticipant.noteToDoctor && (
          <Box
            sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}
            data-testid="note-from-amhp"
          >
            <NoteIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block" }}
              >
                Note from AMHP
              </Typography>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap" }}
                data-testid="note-from-amhp-text"
              >
                {interview.myParticipant.noteToDoctor}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Location */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <LocationIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              Assessment Location
            </Typography>
            {hasAddress ? (
              <Typography variant="body2" data-testid="assessment-location">
                {assessmentLocation}
              </Typography>
            ) : interview.locationUnknown ? (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                Location to be confirmed
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                No location specified
              </Typography>
            )}
          </Box>
        </Box>

        {/* Time slots */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <CalendarIcon fontSize="small" color="action" sx={{ mt: 0.25 }} />
          <Box>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", display: "block" }}
            >
              Proposed Times
            </Typography>
            {interview.timeRanges.length > 0 ? (
              interview.timeRanges.map((range, idx) => (
                <Typography key={idx} variant="body2">
                  {formatInterviewTimeRange(range.start, range.end)}
                </Typography>
              ))
            ) : (
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontStyle: "italic" }}
              >
                No time slots specified
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* Add to calendar button — only shown when there is exactly one time slot */}
      {canAddToCalendar && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 3,
            pt: 2,
            borderTop: "1px solid",
            borderColor: "divider",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<EventIcon fontSize="small" />}
            onClick={handleDownloadCalendarInvite}
            data-testid="add-to-calendar-button"
            sx={{ textTransform: "none" }}
          >
            Add to Calendar
          </Button>
        </Box>
      )}
    </PopupDialog>
  );
}
